import { IdentityConfig, PolicyClaims, ResolvedPolicy, SsoPolicy } from 'types/auth';

export const POLICY_ID_PARAM = 'policyId';

// Workbench names the same value authPolicy, so both spellings are accepted.
export const WORKBENCH_POLICY_ID_PARAM = 'authPolicy';

const readParam = (params: URLSearchParams, name: string): string | undefined => {
  const value = params.get(name);
  const trimmed = value ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
};

// The partner application hands the policy off in the query string, for example
// ?policyId=B2C_1A_FA_PN_WELLS_ACI_Signupsignin
export const parsePolicyId = (search: string): string | undefined => {
  const params = new URLSearchParams(search);
  return readParam(params, POLICY_ID_PARAM) || readParam(params, WORKBENCH_POLICY_ID_PARAM);
};

// Matching is case insensitive because B2C treats policy names that way, but the configured
// spelling is what gets used so the authority URL never contains caller supplied text.
export const findSsoPolicy = (
  policies: readonly SsoPolicy[],
  policyId?: string,
): SsoPolicy | undefined => {
  if (!policyId) return undefined;
  const wanted = policyId.toLowerCase();
  return policies.find(policy => policy.policyId.toLowerCase() === wanted);
};

export const buildAuthority = (identity: IdentityConfig, policyId: string): string =>
  `https://${identity.authorityDomain}/${identity.tenantName}/${policyId}`;

const toResolved = (identity: IdentityConfig, policy: SsoPolicy): ResolvedPolicy => ({
  policyId: policy.policyId,
  authority: buildAuthority(identity, policy.policyId),
  domainHint: policy.domainHint,
});

/*
Decide which B2C policy this browser should authenticate against.

Precedence:
  1. a supported policy named by the partner link
  2. the policy that issued the cached account, so token renewal stays on the same authority
  3. the pre-existing AzureObjectID rule, which routes accounts without that claim to B2E
  4. the default sign in policy
*/
export const resolvePolicy = (
  identity: IdentityConfig,
  requestedPolicyId?: string,
  claims?: PolicyClaims,
): ResolvedPolicy => {
  const requested = findSsoPolicy(identity.ssoPolicies, requestedPolicyId);
  if (requested) return toResolved(identity, requested);

  const issued = findSsoPolicy(identity.ssoPolicies, claims && (claims.tfp || claims.acr));
  if (issued) return toResolved(identity, issued);

  const policyId = claims && !Object.prototype.hasOwnProperty.call(claims, 'AzureObjectID')
    ? identity.b2ePolicyId
    : identity.defaultPolicyId;

  return toResolved(identity, findSsoPolicy(identity.ssoPolicies, policyId) || { policyId });
};
